<template>
    <b-modal
        title-tag="h2"
        title="Email Certificate"
        lazy
        :visible="isOpen"
        no-enforce-focus
    >
        <form>
            <b-form-group label-cols="2" label="To:" label-for="to">
                <!-- do not change the module of vet select: logic handling issue -->
                <recipient-email
                    name="recipient"
                    v-model="recipient"
                    multiple
                    placeholder="Client or Contact Email (required)"
                    @input="values => handleRecipientChangeAlt(values, true)"
                    :key="recipientComponentKey"
                    :client="clientDetails"
                    :filterData="false"
                />
            </b-form-group>

            <b-form-group label-cols="2" label="Subject:" label-for="subject">
                <b-form-input
                    v-model="subject"
                    :placeholder="
                        isLoadingEmailDefaultLetter
                            ? 'Loading...'
                            : 'Subject (optional)'
                    "
                />
            </b-form-group>

            <b-form-group label-cols="2" label="Message:" label-for="message">
                <tinymce
                    :init="tinymceConfig"
                    v-model="message"
                    @onKeyPress="contentChanged"
                />
            </b-form-group>
        </form>

        <template v-slot:modal-footer>
            <loader v-if="isSending" class="m-0" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isSending"
            >
                Cancel
            </b-button>

            <b-button v-if="!isSending" variant="primary" @click="send">
                Send
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import Loader from "@/module/commons/loader";
import tinymce from "@tinymce/tinymce-vue";
import tinymceHelper from "@/module/commons/helper-tinymce"; // helper
import recipientEmail from "@/components/commons/recipients-email";
import CertificateServices from "@/services/certificate";
import EmailMixins from "@/mixins/email";

export default {
    components: {
        Loader,
        tinymce,
        recipientEmail
    },
    mixins: [EmailMixins],
    data() {
        return {
            isOpen: false,
            certificateId: null,
            recipient: [],
            subject: "",
            message: "",
            isLoading: true,
            isSending: false,
            isLoadingEmailDefaultLetter: true,
            clientDetails: null,
            recipientComponentKey: 0,
            emailDefaultLetter: {
                body: null,
                subject: null
            },
            tinymceConfig: {
                ...tinymceHelper.core, // use spread operator to inject all vetter-related configs
                toolbar: false,
                plugins: tinymceHelper.plugins
            },
            isContentChanged: false
        };
    },
    methods: {
        contentChanged() {
            // detect content has been changed by user (once)
            if (!this.isContentChanged) {
                this.isContentChanged = true;
            }
            return true;
        },
        async open(record) {
            this.isOpen = true;
            this.certificateId = record.id;
            await this.fetchSetEmailDefaultCertificate(record);
        },
        close() {
            this.isOpen = false;
        },
        setDefaultRecipient(recipient = {}) {
            this.clientDetails = recipient;
            this.recipientComponentKey++; // force re-render
        },
        constructActorIds(recipients = []) {
            let ids = [];

            for (const recipient of recipients) {
                ids.push(recipient.to_id);
            }

            return ids;
        },
        async send() {
            const firstRecipient = this.recipient[0];

            if (!firstRecipient || this.message === "") {
                return;
            }

            this.isSending = true;

            const successSent = await CertificateServices.certificateEmail(
                this.certificateId,
                {
                    email_to: this.constructActorIds(this.recipient),
                    subject: this.subject,
                    message: this.message,
                    timestamp: this._now().toFormat("X"),
                    send: true
                }
            );

            this.isSending = false;

            if (successSent) {
                this.close();
                this._message("Certificate emailed");
            } else {
                this._message("Error: Unable to send email", "danger");
            }
        },
        async fetchSetEmailDefaultCertificate(record) {
            this.isLoadingEmailDefaultLetter = true;

            const formData = new FormData();
            formData.append("params[0][email_type]", 6);
            formData.append("params[0][business_id]", this._setting.business.id); // prettier-ignore
            formData.append("params[0][client_id]", record.client_id);
            formData.append("params[0][patient_id][]", record.patient_id ? [record.patient_id] : []); // prettier-ignore

            formData.append("params[0][letter_id]", record.id);
            formData.append("params[0][letter_template]", record.template_id);
            formData.append("params[0][letter_title]", record.title);
            formData.append("params[0][skip_variable_replacement]", true);

            const res = await CertificateServices.certificateEmail(
                this.certificateId,
                {
                    send: false
                }
            );

            let template = {};
            if (res.resources && res.code && res.code == 200) {
                template = res.resources;
            }

            this.emailDefaultLetter = {
                body: template.message,
                subject: template.subject
            };

            this.isLoadingEmailDefaultLetter = false;
            this.subject = template.subject;
            this.message = this.setNamesElement(template.message, false);
            console.log("fetchSetEmailDefaultCertificate");
            this.setDefaultRecipient(template.client);
        },
        separateHeaderFromBody(htmlString) {
            // Parse the HTML content
            var parser = new DOMParser();
            var doc = parser.parseFromString(htmlString, "text/html");

            // Get all <p> elements
            var pElements = doc.querySelectorAll("p");

            // Check if there are no paragraphs
            if (pElements.length === 0) {
                return {
                    header: "",
                    body: ""
                };
            }

            // Initialize header and body variables
            var header = "";
            var body = "";

            // Iterate through all paragraphs
            pElements.forEach((p, index) => {
                // If it's the first paragraph, consider it the header
                if (index === 0) {
                    header = p.outerHTML; // This includes the <p> tags themselves
                } else {
                    // The rest is considered the body
                    body += p.outerHTML; // This includes the <p> tags themselves
                }
            });

            // Return an object with the separated header and body
            return {
                header: header,
                body: body
            };
        },
        async handleRecipientChange(values = []) {
            if (values.length === 0 && !this.isContentChanged) {
                this.message = this.emailDefaultLetter.body;
            } else {
                this.handleRecipientChangeAlt(values);
            }
        }
    }
};
</script>
